<script setup lang="ts">
/**
 * UpgradePlanModal — 1:1 порт docs/mockups/modal-upgrade-plan.html (inbox-157).
 *
 * Секции мокапа:
 *   1. Current-card: текущий tier + usage progress bars (ссылки / клики)
 *   2. Period-toggle: Месячная / Годовая (−20% бейдж)
 *   3. Tier-grid: 4 tier'а (Free / Старт / Бизнес / Команда)
 *      - Бизнес помечен UiBadge variant=success «Popular»
 *      - Текущий tier — disabled CTA + «текущий» marker (radio-select disabled
 *        логически: текущий план выбран и dimmed)
 *      - feature-list с check (✓) / cross (✗) iconaми
 *   4. Promo-code input (опц) — добавлено per brief, мокап не имел; в footer
 *   5. Pro-rata calc preview — списание сейчас / следующий платёж
 *   6. Footer: «Связаться по Enterprise» (mailto) + «К оплате» (chain →
 *      modals.open('add-payment-method'))
 *
 * Wire: me.organization_plan (из useMeStore) выбирает current tier.
 *
 * Backend pending: Юкасса / Тинькофф интеграция в bucket H. Сейчас:
 *   - Tier-comparison чисто UI
 *   - "К оплате" chain'ит на add-payment-method modal (когда появится)
 *   - "Связаться по Enterprise" — mailto link
 */

import { computed, ref } from 'vue';
import { useMeStore } from '@/stores/me';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiButton from '@/components/ui/UiButton.vue';

const me = useMeStore();
const modals = useModalStore();

type Period = 'month' | 'year';
type PlanKey = 'free' | 'starter' | 'business' | 'team';

const period = ref<Period>('month');
const selectedPlan = ref<PlanKey | null>(null);
const promoCode = ref('');

interface Tier {
  key: PlanKey;
  name: string;
  priceMonth: number; // ₽
  priceYear: number;  // ₽/мес при годовом
  priceLabel?: string; // override для free / enterprise-style
  features: Array<{ text: string; included: boolean; emph?: boolean }>;
  highlight?: 'recommended';
}

const TIERS: Tier[] = [
  {
    key: 'free', name: 'Free',
    priceMonth: 0, priceYear: 0, priceLabel: '0 ₽ навсегда',
    features: [
      { text: '10 ссылок', included: true },
      { text: '500 кликов / мес', included: true },
      { text: 'Аналитика real-time', included: true },
      { text: 'AI-копайлот', included: false },
    ],
  },
  {
    key: 'starter', name: 'Старт',
    priceMonth: 990, priceYear: 792,
    features: [
      { text: '500 ссылок', included: true },
      { text: '50 000 кликов', included: true },
      { text: 'AI — 50 запросов/мес', included: true },
      { text: 'Smart routing', included: false },
    ],
  },
  {
    key: 'business', name: 'Бизнес',
    priceMonth: 4990, priceYear: 3992,
    highlight: 'recommended',
    features: [
      { text: '10 000 ссылок', included: true, emph: true },
      { text: '500 000 кликов', included: true, emph: true },
      { text: 'AI без лимита + Smart routing', included: true },
      { text: 'Команда до 5 человек', included: true },
    ],
  },
  {
    key: 'team', name: 'Команда',
    priceMonth: 14990, priceYear: 11992,
    features: [
      { text: 'Без лимита ссылок', included: true },
      { text: '5 млн кликов', included: true },
      { text: 'Команда до 30 + SSO', included: true },
      { text: 'SLA 4 часа', included: true },
    ],
  },
];

const currentPlan = computed<PlanKey>(() => {
  const p = me.me?.organization_plan;
  if (p === 'starter' || p === 'business' || p === 'team') return p;
  return 'free';
});

const currentTierLabel = computed(() => {
  const t = TIERS.find((x) => x.key === currentPlan.value);
  return t?.name || 'Free';
});

function tierPrice(t: Tier): string {
  if (t.priceLabel) return t.priceLabel;
  const v = period.value === 'year' ? t.priceYear : t.priceMonth;
  return v.toLocaleString('ru-RU').replace(/,/g, ' ') + ' ₽/мес';
}

function tierCtaLabel(t: Tier): string {
  if (t.key === currentPlan.value) return 'Текущий тариф';
  // Order: free < starter < business < team
  const order: PlanKey[] = ['free', 'starter', 'business', 'team'];
  const cur = order.indexOf(currentPlan.value);
  const target = order.indexOf(t.key);
  if (target < cur) return `Понизить до ${t.name}`;
  return `Перейти на ${t.name}`;
}

function selectTier(t: Tier) {
  if (t.key === currentPlan.value) return; // disabled — нельзя выбрать текущий
  selectedPlan.value = t.key;
}

function proceedToPayment() {
  if (!selectedPlan.value) return;
  // Chain: открываем next modal с payload (полезный нам контекст)
  modals.open('add-payment-method', {
    plan: selectedPlan.value,
    period: period.value,
    promo_code: promoCode.value.trim() || null,
  });
}

const selectedTier = computed<Tier | null>(() => {
  if (!selectedPlan.value) return null;
  return TIERS.find((t) => t.key === selectedPlan.value) || null;
});

const proRataPreview = computed(() => {
  // Mock pro-rata: 18 дней оставшихся → ~60% от месячной цены selected tier'а
  if (!selectedTier.value) return null;
  const monthly = selectedTier.value.priceMonth;
  const proRata = Math.round(monthly * 0.6);
  return {
    nowCharge: proRata,
    nextCharge: period.value === 'year' ? selectedTier.value.priceYear : monthly,
    daysRemaining: 18,
  };
});

function fmt(n: number): string {
  return n.toLocaleString('ru-RU').replace(/,/g, ' ');
}
</script>

<template>
  <UiModal title="Повысить тариф" size="lg" :disable-close="modals.pending" @close="modals.close">
    <template #header>
      <div>
        <h2 class="modal-title">Повысить тариф</h2>
        <p class="modal-sub">
          Апгрейд применяется мгновенно. За оставшийся месяц списываем pro-rata
          разницу. Даунгрейд — со следующего расчётного периода.
        </p>
      </div>
    </template>

    <!-- 1. Current card + usage -->
    <div class="current-card">
      <div class="current-row">
        <div>
          <div class="current-label">Сейчас:</div>
          <div class="current-tier">{{ currentTierLabel }}</div>
        </div>
        <div class="current-usage">
          <div class="usage-row">
            <span>Ссылки</span>
            <span><strong>—</strong> / лимит</span>
          </div>
          <div class="usage-bar"><div class="usage-fill" style="width: 0%"></div></div>
          <div class="usage-row" style="margin-top: 8px;">
            <span>Клики в месяц</span>
            <span><strong>—</strong> / лимит</span>
          </div>
          <div class="usage-bar"><div class="usage-fill" style="width: 0%"></div></div>
        </div>
      </div>
      <div class="current-hint">
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="13"/><line x1="12" y1="16" x2="12" y2="16.5"/></svg>
        Live-данные usage подтянутся когда billing-API будет в наличии.
      </div>
    </div>

    <!-- 2. Period toggle -->
    <div class="period-row">
      <div class="period-toggle" role="radiogroup" aria-label="Период">
        <button
          type="button"
          :class="['period-opt', { active: period === 'month' }]"
          @click="period = 'month'"
        >Месячная</button>
        <button
          type="button"
          :class="['period-opt', { active: period === 'year' }]"
          @click="period = 'year'"
        >
          Годовая
          <span class="save">−20%</span>
        </button>
      </div>
    </div>

    <!-- 3. Tier grid (4 columns) -->
    <div class="tier-grid" role="radiogroup" aria-label="Выберите тариф">
      <article
        v-for="t in TIERS"
        :key="t.key"
        :class="[
          'tier',
          { current: t.key === currentPlan },
          { recommended: t.highlight === 'recommended' },
          { selected: selectedPlan === t.key },
        ]"
        role="radio"
        :aria-checked="selectedPlan === t.key"
        :aria-disabled="t.key === currentPlan"
        tabindex="0"
        @click="selectTier(t)"
        @keydown.space.prevent="selectTier(t)"
        @keydown.enter.prevent="selectTier(t)"
      >
        <UiBadge v-if="t.key === currentPlan" variant="default" class="tier-marker">текущий</UiBadge>
        <UiBadge v-else-if="t.highlight === 'recommended'" variant="success" class="tier-marker rec">Popular</UiBadge>

        <div class="tier-name">{{ t.name }}</div>
        <div class="tier-price">{{ tierPrice(t) }}</div>

        <ul class="tier-features">
          <li v-for="(f, i) in t.features" :key="i" :class="{ no: !f.included }">
            <svg v-if="f.included" class="ic" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 13l4 4L19 7"/></svg>
            <svg v-else class="ic" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
            <span :class="{ emph: f.emph }">{{ f.text }}</span>
          </li>
        </ul>

        <button
          type="button"
          class="tier-cta"
          :class="{ primary: t.highlight === 'recommended' && t.key !== currentPlan, selected: selectedPlan === t.key }"
          :disabled="t.key === currentPlan"
          @click.stop="selectTier(t)"
        >{{ tierCtaLabel(t) }}</button>
      </article>
    </div>

    <!-- 4. Promo-code (опц, per brief — мокап не имеет) -->
    <div class="promo-row">
      <label class="promo-label" for="promo-code">Промокод <span class="hint">— опционально</span></label>
      <div class="promo-input-wrap">
        <input
          id="promo-code"
          v-model="promoCode"
          class="input"
          type="text"
          placeholder="LP-2026-PROMO"
          autocomplete="off"
        />
        <button type="button" class="btn-mini" :disabled="!promoCode.trim()">Применить</button>
      </div>
    </div>

    <!-- 5. Pro-rata preview (показывается если выбран tier) -->
    <div v-if="proRataPreview && selectedTier" class="pro-rata">
      <div class="pro-rata-h">
        <span>При переходе на <strong>{{ selectedTier.name }}</strong> сегодня:</span>
        <a href="/pricing" class="ext-link">Сравнить всё →</a>
      </div>
      <div class="pro-rata-rows">
        <div class="pro-rata-row">
          <span class="key">Списание сейчас</span>
          <span class="val">{{ fmt(proRataPreview.nowCharge) }} ₽</span>
          <span class="hint">pro-rata за {{ proRataPreview.daysRemaining }} дней</span>
        </div>
        <div class="pro-rata-row">
          <span class="key">Следующий платёж</span>
          <span class="val">{{ fmt(proRataPreview.nextCharge) }} ₽</span>
          <span class="hint">после релиза billing-API</span>
        </div>
      </div>
    </div>

    <template #footer>
      <a href="mailto:sales@link-platform.ru" class="ent-link">Связаться по Enterprise</a>
      <div class="foot-spacer"></div>
      <UiButton variant="ghost" :disabled="modals.pending" @click="modals.close">Закрыть</UiButton>
      <UiButton
        variant="primary"
        :disabled="!selectedPlan || modals.pending"
        @click="proceedToPayment"
      >К оплате</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.modal-title { font-size: 18px; letter-spacing: -0.01em; margin: 0 0 4px; }
.modal-sub { font-size: 13px; color: var(--fg-muted, #9aa5b1); line-height: 1.45; margin: 0; }

/* Current card */
.current-card {
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 16px;
}
.current-row {
  display: grid; grid-template-columns: auto 1fr; gap: 24px; align-items: center;
}
.current-label { font-size: 11px; color: var(--fg-muted, #9aa5b1); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.current-tier { font-size: 18px; font-weight: 700; color: var(--fg, #e6eaf0); margin-top: 2px; }
.current-usage { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.usage-row { display: flex; justify-content: space-between; font-size: 12.5px; color: var(--fg-muted, #9aa5b1); }
.usage-row strong { color: var(--fg, #e6eaf0); font-weight: 600; font-variant-numeric: tabular-nums; }
.usage-bar {
  height: 5px; background: var(--bg, #0f1620);
  border-radius: 100px; overflow: hidden;
}
.usage-fill { height: 100%; background: var(--accent, #4f8cff); border-radius: 100px; }
.current-hint {
  margin-top: 12px; padding-top: 12px;
  border-top: 1px solid var(--border, #2a3340);
  display: flex; gap: 8px; align-items: flex-start;
  font-size: 12.5px; color: var(--fg-muted, #9aa5b1);
}

/* Period toggle */
.period-row { display: flex; justify-content: center; margin-bottom: 16px; }
.period-toggle {
  display: inline-flex;
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  border-radius: 8px; padding: 3px;
}
.period-opt {
  background: transparent; border: 0;
  padding: 6px 14px; border-radius: 6px;
  color: var(--fg-muted, #9aa5b1); font: inherit; font-size: 13px;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
}
.period-opt.active { background: rgba(79,140,255,0.15); color: var(--accent-hi, #6f9eff); }
.save {
  font-size: 10.5px; padding: 1px 6px; border-radius: 10px;
  background: rgba(61,216,127,0.15); color: #3dd87f;
}

/* Tier grid */
.tier-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
  margin-bottom: 16px;
}
.tier {
  position: relative;
  display: flex; flex-direction: column; gap: 10px;
  padding: 18px 14px;
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 10px;
  cursor: pointer; transition: 0.15s;
}
.tier:hover { border-color: rgba(79,140,255,0.4); }
.tier.current { opacity: 0.7; cursor: default; border-color: var(--border, #2a3340); }
.tier.current:hover { border-color: var(--border, #2a3340); }
.tier.recommended { border-color: var(--accent, #4f8cff); box-shadow: 0 0 0 1px rgba(79,140,255,0.3); }
.tier.selected { box-shadow: 0 0 0 2px var(--accent, #4f8cff); border-color: var(--accent, #4f8cff); }
.tier.selected.current { box-shadow: none; }

.tier-marker {
  position: absolute; top: -8px; right: 14px;
  font-size: 10.5px;
}
.tier-name {
  font-size: 13px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--fg, #e6eaf0);
}
.tier-price {
  font-size: 22px; font-weight: 700; letter-spacing: -0.01em;
  color: var(--fg, #e6eaf0);
}
.tier-features { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.tier-features li {
  display: flex; gap: 6px; align-items: flex-start;
  font-size: 12.5px; line-height: 1.4;
  color: var(--fg, #e6eaf0);
}
.tier-features li.no { color: var(--fg-dim, #6b7480); }
.tier-features li .ic { color: #3dd87f; flex-shrink: 0; margin-top: 2px; }
.tier-features li.no .ic { color: var(--fg-dim, #6b7480); }
.tier-features li .emph { font-weight: 700; }

.tier-cta {
  margin-top: auto;
  padding: 8px 12px;
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  color: var(--fg, #e6eaf0);
  border-radius: 7px; font: inherit; font-size: 12.5px;
  cursor: pointer;
}
.tier-cta:hover:not(:disabled) { border-color: var(--accent, #4f8cff); }
.tier-cta.primary {
  background: var(--accent, #4f8cff);
  border-color: var(--accent, #4f8cff);
  color: #fff;
}
.tier-cta.selected { box-shadow: 0 0 0 2px rgba(79,140,255,0.3); }
.tier-cta:disabled { opacity: 0.55; cursor: not-allowed; }

/* Promo code */
.promo-row { margin-bottom: 16px; }
.promo-label { font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); display: block; margin-bottom: 6px; }
.hint { color: var(--fg-dim, #6b7480); font-weight: 400; }
.promo-input-wrap { display: flex; gap: 8px; }
.input {
  padding: 9px 12px;
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  border-radius: 7px;
  color: var(--fg, #e6eaf0); font: inherit; font-size: 14px;
  flex: 1;
}
.input:focus { outline: none; border-color: var(--accent, #4f8cff); }
.btn-mini {
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  color: var(--fg-muted, #9aa5b1);
  padding: 4px 14px; border-radius: 7px;
  font: inherit; font-size: 13px; cursor: pointer;
}
.btn-mini:not(:disabled):hover { border-color: var(--accent, #4f8cff); color: var(--accent-hi, #6f9eff); }
.btn-mini:disabled { opacity: 0.55; cursor: not-allowed; }

/* Pro-rata preview */
.pro-rata {
  background: rgba(79,140,255,0.06);
  border: 1px solid rgba(79,140,255,0.3);
  border-radius: 10px; padding: 14px 16px;
  margin-bottom: 8px;
}
.pro-rata-h {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 13px; color: var(--fg-muted, #9aa5b1); margin-bottom: 10px;
}
.pro-rata-h strong { color: var(--accent-hi, #6f9eff); font-weight: 700; }
.ext-link { color: var(--accent-hi, #6f9eff); font-size: 12.5px; text-decoration: none; }
.ext-link:hover { text-decoration: underline; }
.pro-rata-rows { display: flex; flex-direction: column; gap: 8px; }
.pro-rata-row {
  display: grid; grid-template-columns: 1fr auto 1fr;
  gap: 12px; align-items: baseline;
  font-size: 13px;
}
.pro-rata-row .key { color: var(--fg, #e6eaf0); }
.pro-rata-row .val { font-weight: 700; color: var(--accent-hi, #6f9eff); font-variant-numeric: tabular-nums; }
.pro-rata-row .hint { color: var(--fg-muted, #9aa5b1); font-size: 12px; text-align: right; }

/* Footer */
.ent-link {
  color: var(--fg-muted, #9aa5b1); font-size: 13px;
  text-decoration: none;
}
.ent-link:hover { color: var(--accent-hi, #6f9eff); }
.foot-spacer { flex: 1; }

@media (max-width: 760px) {
  .tier-grid { grid-template-columns: repeat(2, 1fr); }
  .pro-rata-row { grid-template-columns: 1fr 1fr; }
  .pro-rata-row .hint { display: none; }
}
@media (max-width: 480px) {
  .tier-grid { grid-template-columns: 1fr; }
}
</style>
